import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Type, Bold, Italic, Underline, Link as LinkIcon, Image, Video, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  image: HTMLImageElement | null;
}

export default function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = "เริ่มพิมพ์...",
  minHeight = "200px"
}: RichTextEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    image: null
  });

  // Handle image alignment
  const handleImageAlign = (alignment: 'left' | 'center' | 'right') => {
    if (!contextMenu.image) return;

    const img = contextMenu.image;
    const parent = img.parentElement;

    // Remove existing alignment classes
    img.classList.remove('ql-align-left', 'ql-align-center', 'ql-align-right');
    if (parent) {
      parent.classList.remove('ql-align-left', 'ql-align-center', 'ql-align-right');
    }

    // Add new alignment class
    const alignClass = `ql-align-${alignment}`;
    img.classList.add(alignClass);
    if (parent) {
      parent.classList.add(alignClass);
    }

    // Trigger change event
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Close context menu
    setContextMenu({ visible: false, x: 0, y: 0, image: null });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, image: null });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.visible]);

  // Add tooltips and custom color pickers
  useEffect(() => {
    const setupToolbar = () => {
      const toolbar = document.querySelector('.rich-text-editor-wrapper .ql-toolbar');
      if (!toolbar) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      // Button tooltips
      const tooltips: { [key: string]: string } = {
        '.ql-bold': 'ตัวหนา (Bold)',
        '.ql-italic': 'ตัวเอียง (Italic)',
        '.ql-underline': 'ขีดเส้นใต้ (Underline)',
        '.ql-strike': 'ขีดฆ่า (Strikethrough)',
        '.ql-link': 'แทรกลิงก์ (Link)',
        '.ql-image': 'แทรกรูปภาพ (Image)',
        '.ql-video': 'แทรกวิดีโอ (Video)',
        '.ql-clean': 'ล้างรูปแบบ (Clear Format)',
        '.ql-list[value="ordered"]': 'รายการลำดับเลข (Numbered List)',
        '.ql-list[value="bullet"]': 'รายการจุด (Bullet List)',
      };

      Object.entries(tooltips).forEach(([selector, tooltip]) => {
        const button = toolbar.querySelector(selector);
        if (button) {
          button.setAttribute('data-tooltip', tooltip);
        }
      });

      // Size picker tooltip
      const sizePicker = toolbar.querySelector('.ql-size');
      if (sizePicker) {
        sizePicker.setAttribute('data-tooltip', 'ขนาดตัวอักษร (Font Size)');
      }

      const alignPicker = toolbar.querySelector('.ql-align');
      if (alignPicker) {
        alignPicker.setAttribute('data-tooltip', 'จัดตำแหน่ง (Alignment)');
      }

      // Custom color picker for text color
      const colorPicker = toolbar.querySelector('.ql-color') as HTMLElement;
      if (colorPicker && !colorPicker.querySelector('.custom-color-input')) {
        // Hide default picker options
        const pickerOptions = colorPicker.querySelector('.ql-picker-options');
        if (pickerOptions) {
          (pickerOptions as HTMLElement).style.display = 'none';
        }

        // Create hidden color input
        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.className = 'custom-color-input';
        colorInput.value = '#000000';
        colorInput.style.position = 'absolute';
        colorInput.style.opacity = '0';
        colorInput.style.width = '0';
        colorInput.style.height = '0';
        colorInput.style.pointerEvents = 'none';

        // Create color indicator on picker label
        const pickerLabel = colorPicker.querySelector('.ql-picker-label') as HTMLElement;
        if (pickerLabel) {
          // Add color indicator
          const colorIndicator = document.createElement('span');
          colorIndicator.className = 'color-indicator';
          colorIndicator.style.cssText = `
            position: absolute;
            bottom: 2px;
            left: 50%;
            transform: translateX(-50%);
            width: 14px;
            height: 3px;
            background: #000000;
            border-radius: 1px;
          `;
          pickerLabel.appendChild(colorIndicator);

          // Click handler to open color picker
          pickerLabel.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            colorInput.click();
          });

          // Update color when selected
          colorInput.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            const color = target.value;
            colorIndicator.style.background = color;
            editor.format('color', color);
          });

          colorPicker.appendChild(colorInput);
        }

        colorPicker.setAttribute('data-tooltip', 'สีตัวอักษร (Text Color)');
      }

      // Custom color picker for background color
      const backgroundPicker = toolbar.querySelector('.ql-background') as HTMLElement;
      if (backgroundPicker && !backgroundPicker.querySelector('.custom-color-input')) {
        // Hide default picker options
        const pickerOptions = backgroundPicker.querySelector('.ql-picker-options');
        if (pickerOptions) {
          (pickerOptions as HTMLElement).style.display = 'none';
        }

        // Create hidden color input
        const bgInput = document.createElement('input');
        bgInput.type = 'color';
        bgInput.className = 'custom-color-input';
        bgInput.value = '#ffff00';
        bgInput.style.position = 'absolute';
        bgInput.style.opacity = '0';
        bgInput.style.width = '0';
        bgInput.style.height = '0';
        bgInput.style.pointerEvents = 'none';

        // Create color indicator on picker label
        const pickerLabel = backgroundPicker.querySelector('.ql-picker-label') as HTMLElement;
        if (pickerLabel) {
          // Add color indicator
          const colorIndicator = document.createElement('span');
          colorIndicator.className = 'color-indicator';
          colorIndicator.style.cssText = `
            position: absolute;
            bottom: 2px;
            left: 50%;
            transform: translateX(-50%);
            width: 14px;
            height: 3px;
            background: #ffff00;
            border-radius: 1px;
          `;
          pickerLabel.appendChild(colorIndicator);

          // Click handler to open color picker
          pickerLabel.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            bgInput.click();
          });

          // Update color when selected
          bgInput.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            const color = target.value;
            colorIndicator.style.background = color;
            editor.format('background', color);
          });

          backgroundPicker.appendChild(bgInput);
        }

        backgroundPicker.setAttribute('data-tooltip', 'สีพื้นหลัง (Background Color)');
      }
    };

    // Run after a short delay to ensure toolbar is rendered
    const timer = setTimeout(setupToolbar, 100);
    return () => clearTimeout(timer);
  }, []);

  // Make images resizable and handle alignment
  useEffect(() => {
    const makeImagesResizable = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const editorElement = editor.root;
      const images = editorElement.querySelectorAll('img');

      images.forEach((img: HTMLImageElement) => {
        // Skip if already made resizable
        if (img.style.cursor === 'nwse-resize') return;

        // Make image resizable
        img.style.cursor = 'nwse-resize';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.draggable = false;

        // Handle alignment by wrapping image in a paragraph with alignment class
        const parent = img.parentElement;
        if (parent && parent.tagName === 'P') {
          // Check if parent has alignment class
          const alignClass = Array.from(parent.classList).find(cls => cls.startsWith('ql-align-'));
          if (alignClass) {
            // Apply alignment to image
            img.classList.add(alignClass);
          }
        }

        let isResizing = false;
        let startX = 0;
        let startY = 0;
        let startWidth = 0;

        const onMouseDown = (e: MouseEvent) => {
          // Only resize if clicking near the bottom-right corner (within 20px)
          const rect = img.getBoundingClientRect();
          const offsetX = e.clientX - rect.left;
          const offsetY = e.clientY - rect.top;
          
          if (offsetX > rect.width - 20 && offsetY > rect.height - 20) {
            e.preventDefault();
            e.stopPropagation();
            isResizing = true;
            startX = e.clientX;
            startY = e.clientY;
            startWidth = img.offsetWidth;
            
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
          }
        };

        const onContextMenu = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            image: img
          });
        };

        const onMouseMove = (e: MouseEvent) => {
          if (!isResizing) return;
          
          const deltaX = e.clientX - startX;
          const newWidth = startWidth + deltaX;
          
          // Set minimum and maximum width
          if (newWidth >= 100 && newWidth <= editorElement.offsetWidth) {
            img.style.width = newWidth + 'px';
            img.setAttribute('width', newWidth.toString());
          }
        };

        const onMouseUp = () => {
          isResizing = false;
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          
          // Trigger change event to save the new size
          const editor = quillRef.current?.getEditor();
          if (editor) {
            editor.root.dispatchEvent(new Event('input', { bubbles: true }));
          }
        };

        img.addEventListener('mousedown', onMouseDown);
        img.addEventListener('contextmenu', onContextMenu);
      });
    };

    // Run on mount and when content changes
    makeImagesResizable();
    
    // Set up mutation observer to handle dynamically added images
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const observer = new MutationObserver(makeImagesResizable);
      observer.observe(editor.root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      return () => observer.disconnect();
    }
  }, [value]);

  // Custom toolbar configuration
  const modules = {
    toolbar: {
      container: [
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image', 'video'],
        ['clean']
      ]
    }
  };

  const formats = [
    'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'bullet',
    'link', 'image', 'video',
    'width', 'height', 'style'
  ];

  return (
    <div className="rich-text-editor-wrapper">
      <style>{`
        .rich-text-editor-wrapper .quill {
          background: white;
          border-radius: 12px;
          border: 2px solid #E5E7EB;
          transition: border-color 0.2s;
        }
        
        .rich-text-editor-wrapper .quill:focus-within {
          border-color: #3B82F6;
          outline: none;
        }
        
        .rich-text-editor-wrapper .ql-toolbar {
          border: none;
          border-bottom: 2px solid #E5E7EB;
          border-radius: 12px 12px 0 0;
          background: #F9FAFB;
          padding: 12px;
        }
        
        .rich-text-editor-wrapper .ql-container {
          border: none;
          font-size: 16px;
          min-height: ${minHeight};
          border-radius: 0 0 12px 12px;
        }
        
        .rich-text-editor-wrapper .ql-editor {
          min-height: ${minHeight};
          padding: 16px;
          font-family: inherit;
        }
        
        .rich-text-editor-wrapper .ql-editor.ql-blank::before {
          color: #9CA3AF;
          font-style: normal;
        }
        
        /* Toolbar button styles */
        .rich-text-editor-wrapper .ql-toolbar button {
          width: 32px;
          height: 32px;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s;
          position: relative;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover {
          background: #E5E7EB;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button.ql-active {
          background: #DBEAFE;
          color: #3B82F6;
        }
        
        /* Tooltip styles */
        .rich-text-editor-wrapper .ql-toolbar button::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: -35px;
          left: 50%;
          transform: translateX(-50%);
          background: #1F2937;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          z-index: 1000;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover::after {
          opacity: 1;
        }
        
        .rich-text-editor-wrapper .ql-toolbar .ql-picker::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: -35px;
          left: 50%;
          transform: translateX(-50%);
          background: #1F2937;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          z-index: 1000;
        }
        
        .rich-text-editor-wrapper .ql-toolbar .ql-picker:hover::after {
          opacity: 1;
        }
        
        .rich-text-editor-wrapper .ql-toolbar .ql-stroke {
          stroke: #374151;
        }
        
        .rich-text-editor-wrapper .ql-toolbar .ql-fill {
          fill: #374151;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover .ql-stroke {
          stroke: #1F2937;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button:hover .ql-fill {
          fill: #1F2937;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button.ql-active .ql-stroke {
          stroke: #3B82F6;
        }
        
        .rich-text-editor-wrapper .ql-toolbar button.ql-active .ql-fill {
          fill: #3B82F6;
        }
        
        /* Picker styles */
        .rich-text-editor-wrapper .ql-toolbar .ql-picker {
          border-radius: 6px;
          transition: all 0.2s;
        }
        
        .rich-text-editor-wrapper .ql-toolbar .ql-picker:hover {
          background: #E5E7EB;
        }
        
        .rich-text-editor-wrapper .ql-toolbar .ql-picker-label {
          padding: 4px 8px;
          border: none;
        }
        
        /* Content styles */
        .rich-text-editor-wrapper .ql-editor strong {
          font-weight: 700 !important;
        }
        
        .rich-text-editor-wrapper .ql-editor em {
          font-style: italic !important;
        }
        
        .rich-text-editor-wrapper .ql-editor u {
          text-decoration: underline !important;
        }
        
        .rich-text-editor-wrapper .ql-editor h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        
        .rich-text-editor-wrapper .ql-editor h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        
        .rich-text-editor-wrapper .ql-editor h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        
        .rich-text-editor-wrapper .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
          cursor: nwse-resize;
          position: relative;
          transition: box-shadow 0.2s;
          display: block;
        }
        
        .rich-text-editor-wrapper .ql-editor img:hover {
          box-shadow: 0 0 0 2px #3B82F6;
        }
        
        /* Image alignment - both via parent and direct class */
        .rich-text-editor-wrapper .ql-editor .ql-align-left img,
        .rich-text-editor-wrapper .ql-editor img.ql-align-left,
        .rich-text-editor-wrapper .ql-editor p:not([class*="ql-align"]) img {
          margin: 8px 0 8px 0 !important;
          display: block !important;
        }
        
        .rich-text-editor-wrapper .ql-editor .ql-align-center img,
        .rich-text-editor-wrapper .ql-editor img.ql-align-center {
          margin: 8px auto !important;
          display: block !important;
        }
        
        .rich-text-editor-wrapper .ql-editor .ql-align-right img,
        .rich-text-editor-wrapper .ql-editor img.ql-align-right {
          margin: 8px 0 8px auto !important;
          display: block !important;
        }
        
        .rich-text-editor-wrapper .ql-editor p.ql-align-center {
          text-align: center;
        }
        
        .rich-text-editor-wrapper .ql-editor p.ql-align-left {
          text-align: left;
        }
        
        .rich-text-editor-wrapper .ql-editor p.ql-align-right {
          text-align: right;
        }
        
        .rich-text-editor-wrapper .ql-editor video {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
        }
        
        .rich-text-editor-wrapper .ql-editor a {
          color: #3B82F6;
          text-decoration: underline;
        }
        
        .rich-text-editor-wrapper .ql-editor a:hover {
          color: #2563EB;
        }
        
        /* Context Menu */
        .image-context-menu {
          position: fixed;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 4px;
          z-index: 10000;
          min-width: 160px;
        }
        
        .image-context-menu button {
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: white;
          text-align: left;
          cursor: pointer;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #374151;
          transition: background 0.2s;
        }
        
        .image-context-menu button:hover {
          background: #F3F4F6;
        }
        
        .image-context-menu button svg {
          width: 16px;
          height: 16px;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .rich-text-editor-wrapper .ql-toolbar {
            padding: 8px;
          }
          
          .rich-text-editor-wrapper .ql-toolbar button {
            width: 28px;
            height: 28px;
          }
          
          .rich-text-editor-wrapper .ql-container {
            font-size: 14px;
          }
          
          .rich-text-editor-wrapper .ql-editor {
            padding: 12px;
          }
        }
      `}</style>
      
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />

      {/* Context Menu for Image Alignment */}
      {contextMenu.visible && (
        <div 
          className="image-context-menu"
          style={{ 
            left: `${contextMenu.x}px`, 
            top: `${contextMenu.y}px` 
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={() => handleImageAlign('left')}>
            <AlignLeft />
            <span>ชิดซ้าย</span>
          </button>
          <button onClick={() => handleImageAlign('center')}>
            <AlignCenter />
            <span>กึ่งกลาง</span>
          </button>
          <button onClick={() => handleImageAlign('right')}>
            <AlignRight />
            <span>ชิดขวา</span>
          </button>
        </div>
      )}
      
      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Type className="w-3 h-3" />
          <span>รองรับ HTML</span>
        </div>
        <div className="flex items-center gap-1">
          <LinkIcon className="w-3 h-3" />
          <span>ใส่ลิงค์ได้</span>
        </div>
        <div className="flex items-center gap-1">
          <Image className="w-3 h-3" />
          <span>แทรกรูปภาพ</span>
        </div>
        <div className="flex items-center gap-1">
          <Video className="w-3 h-3" />
          <span>แทรกวิดีโอ</span>
        </div>
      </div>
    </div>
  );
}
